import { ref, watch, onUnmounted, nextTick, type Ref } from 'vue'
import { SENTRY_FEEDBACK_HOST_ID } from '@/constants/sentryFeedback'

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Element that should receive focus when the trap activates, instead of the
 * first focusable one. Mark the primary control of a dialog with it, e.g. the
 * search box of a picker: `<input data-autofocus />`.
 */
const AUTOFOCUS_SELECTOR = '[data-autofocus]'

/**
 * Focusable, but never the element that receives focus on activation — a header
 * control that leaves what the dialog is for, like the feedback trigger (#4694),
 * which would otherwise come before everything in the body.
 */
const AUTOFOCUS_SKIP_SELECTOR = '[data-autofocus-skip]'

/**
 * Whether a key event comes from the Sentry feedback dialog. It opens on top of
 * the trapped container (#4694) and its keys are its own: an Escape typed there
 * would otherwise close the overlay underneath, with whatever was filled in on it.
 * The dialog lives in a shadow root, so the event's target is retargeted to the
 * host element.
 */
function isFromFeedbackDialog(e: KeyboardEvent): boolean {
  return e.target instanceof Element && e.target.closest(`#${SENTRY_FEEDBACK_HOST_ID}`) !== null
}

/**
 * Traps focus within a container element while active.
 * Implements WCAG 2.1 Focus Order (2.4.3) requirements for modal dialogs.
 *
 * - Focus moves to the `[data-autofocus]` element when activated, or to the
 *   first focusable element not marked `[data-autofocus-skip]` when there is none
 * - Tab cycling stays within the container
 * - Shift+Tab from first element goes to last
 * - Focus returns to trigger element on deactivation, and when the container is
 *   unmounted while still active
 * - Escape key triggers optional close callback
 * - Keys pressed in the Sentry feedback dialog are left alone
 */
export function useFocusTrap(
  containerRef: Ref<HTMLElement | null>,
  isActive: Ref<boolean>,
  options?: { onEscape?: () => void }
) {
  const previousActiveElement = ref<HTMLElement | null>(null)

  function getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
  }

  function trapFocus(e: KeyboardEvent) {
    if (!containerRef.value || isFromFeedbackDialog(e)) return

    if (e.key === 'Escape' && options?.onEscape) {
      e.preventDefault()
      options.onEscape()
      return
    }

    if (e.key !== 'Tab') return

    const focusable = getFocusableElements(containerRef.value)
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (!first || !last) return

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  function restoreFocus() {
    const previous = previousActiveElement.value
    previousActiveElement.value = null
    // A trigger that was removed while the dialog was open (a row that is gone
    // after the edit) cannot take focus back; leave it on the body instead.
    if (previous?.isConnected) previous.focus()
  }

  // Immediate: a dialog whose first render is already open (mounted behind a
  // `v-if`) must be trapped too, not only one that toggles open later.
  watch(
    isActive,
    (active) => {
      if (active) {
        previousActiveElement.value = document.activeElement as HTMLElement
        nextTick(() => {
          const container = containerRef.value
          if (!container) return
          const target =
            container.querySelector<HTMLElement>(AUTOFOCUS_SELECTOR) ??
            getFocusableElements(container).find((el) => !el.matches(AUTOFOCUS_SKIP_SELECTOR))
          target?.focus()
        })
        document.addEventListener('keydown', trapFocus)
      } else {
        document.removeEventListener('keydown', trapFocus)
        restoreFocus()
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    document.removeEventListener('keydown', trapFocus)
    if (isActive.value) restoreFocus()
  })
}
